/** @format */

import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ResourceLink } from "../../../types";
import { ReactComponent as ChevronUp } from "bootstrap-icons/icons/chevron-up.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { getUniqueCategories } from "../../../utils/functions";
import { DEFAULT_CATEGORY_NAME } from "./ResourceLinksReducer";

type Props = {
  linksOriginal: ResourceLink[];
  updateLinks?: (links: ResourceLink[]) => void;
  watchLinks?: (links: ResourceLink[]) => void;
};

// Wrapper so that React knows the state has changed when this is updated...
type links2DWrapper = {
  links: ResourceLink[][];
};

/**
 * @param linksOriginal the links to be reordered
 * @param updateLinks a function that is passed the reordered links
 */
const ResourceLinksOrder: React.FC<Props> = ({
  linksOriginal,
  updateLinks,
  watchLinks
}) => {
  const { t: _ } = useTranslation();
  const [links2D, setLinks2D] = useState<links2DWrapper>({
    links: [[]],
  });

  useEffect(() => {
    if (watchLinks === undefined) return;
    watchLinks(links2D.links.flat())
  }, [links2D, watchLinks])

  const canMoveUpLink = (linkIndex: number): boolean => {
    return linkIndex !== 0;
  };
  const moveLinkUp = (catIndex: number, linkIndex: number) => {
    if (!canMoveUpLink(linkIndex)) return;

    let newCatLinks = links2D.links[catIndex];

    // Swap link index with the previous index
    [newCatLinks[linkIndex - 1], newCatLinks[linkIndex]] = [
      newCatLinks[linkIndex],
      newCatLinks[linkIndex - 1],
    ];

    let _links2D = links2D.links;
    _links2D[catIndex] = newCatLinks;
    setLinks2D({ links: _links2D });
  };

  const canMoveDownLink = (catIndex: number, linkIndex: number): boolean => {
    return linkIndex !== links2D.links[catIndex].length - 1;
  };
  const moveLinkDown = (catIndex: number, linkIndex: number) => {
    if (!canMoveDownLink(catIndex, linkIndex)) return;

    let newCatLinks = links2D.links[catIndex];

    // Swap link index with the next element
    [newCatLinks[linkIndex], newCatLinks[linkIndex + 1]] = [
      newCatLinks[linkIndex + 1],
      newCatLinks[linkIndex],
    ];

    let _links2D = links2D.links;
    _links2D[catIndex] = newCatLinks;
    setLinks2D({ links: _links2D });
  };

  const canMoveUpCat = (catIndex: number) => {
    return catIndex !== 1; // beacuse none is 0
  };
  const moveCatUp = (catIndex: number) => {
    if (!canMoveUpCat(catIndex)) return;
    let newCats = links2D.links;

    // Swap cat with previous element
    [newCats[catIndex - 1], newCats[catIndex]] = [
      newCats[catIndex],
      newCats[catIndex - 1],
    ];
    setLinks2D({ links: newCats });
  };

  const canMoveDownCat = (catIndex: number) => {
    return catIndex !== links2D.links.length - 1;
  };
  const moveCatDown = (catIndex: number) => {
    if (!canMoveDownCat(catIndex)) return;
    let newCats = links2D.links;

    // Swap cat with next cat
    [newCats[catIndex], newCats[catIndex + 1]] = [
      newCats[catIndex + 1],
      newCats[catIndex],
    ];
    setLinks2D({ links: newCats });
  };

  /**
   * convert links2D back to a flat array and pass to updateLinks prop
   */
  const flatten2D = () => {
    if (updateLinks === undefined) return;
    updateLinks(links2D.links.flat());
  };

  /**
   * convert the orignal links into a 2D array by category when the original link prop changes
   */
  useEffect(() => {
    let cats = getUniqueCategories(linksOriginal);
    if (!cats.includes(DEFAULT_CATEGORY_NAME))
      cats = [DEFAULT_CATEGORY_NAME, ...cats];

    let links2DTemp: ResourceLink[][] = [[]];

    cats.forEach((cat, index) => {
      const matchedLinks = linksOriginal.filter(
        (link) => link.category === cat,
      );

      links2DTemp[index] = matchedLinks;
    });

    setLinks2D({ links: links2DTemp });
  }, [linksOriginal, _]);

  return (
    <div className="mb-4">
      <h5>{_("Edit Order")}</h5>
      {links2D.links.length > 0 &&
        links2D.links.map((category, catIndex) => {
          return (
            <div key={catIndex} className="card mb-3">
              <div className="fw-bold card-header">
                {catIndex === 0 ? DEFAULT_CATEGORY_NAME : category[0].category}
                {catIndex > 0 && ( // Ignore none category
                  <div className="float-end me-2">
                    <ChevronUp
                      type={canMoveUpCat(catIndex) ? "Button" : ""}
                      className={
                        canMoveUpCat(catIndex) ? "me-2" : "me-2 opacity-25"
                      }
                      onClick={() => moveCatUp(catIndex)}
                    />
                    <ChevronDown
                      type={canMoveDownCat(catIndex) ? "Button" : ""}
                      className={canMoveDownCat(catIndex) ? "" : "opacity-25"}
                      onClick={() => moveCatDown(catIndex)}
                    />
                  </div>
                )}
              </div>

              <div className="card-text container">
                {category.length > 0 &&
                  category.map((link, linkIndex) => {
                    return (
                      <div
                        key={linkIndex}
                        className="mb-3 mt-2 row align-items-center"
                      >
                        <div className="col" title={link.label}>
                          {link.label}
                        </div>
                        <div className="col border-end text-center">
                          <ChevronUp
                            type={canMoveUpLink(linkIndex) ? "Button" : ""}
                            className={
                              !canMoveUpLink(linkIndex) ? "opacity-25" : ""
                            }
                            onClick={() => moveLinkUp(catIndex, linkIndex)}
                          />
                        </div>
                        <div className="col text-center">
                          <ChevronDown
                            type={
                              canMoveDownLink(catIndex, linkIndex)
                                ? "Button"
                                : ""
                            }
                            className={
                              !canMoveDownLink(catIndex, linkIndex)
                                ? "opacity-25"
                                : ""
                            }
                            onClick={() => moveLinkDown(catIndex, linkIndex)}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}

      {updateLinks !== undefined && (
        <>
          <Button variant="editing" onClick={() => flatten2D()}>
            {_("Edit Links")}
          </Button>
        </>
      )}
    </div>
  );
};

export default ResourceLinksOrder;
